import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import NavHeader from '../components/NavHeader';
import { 
  Trophy, Medal, Award, ChevronUp, ChevronDown, ExternalLink, 
  Linkedin, Calendar, Rocket, MessageSquare, Send, X, Loader2, Star
} from 'lucide-react';
import { 
  getEventsFast, toggleUpvote, getBatchUpvoteStatus,
  getComments, addComment 
} from '../services/api';

// Achievement Badge Component
const AchievementBadge = ({ achievement }) => {
  if (!achievement) return null;
  
  const badges = {
    winner: { icon: Trophy, label: 'Winner', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
    top2: { icon: Medal, label: 'Top 2', className: 'bg-slate-400/20 text-slate-300 border-slate-400/50' },
    top3: { icon: Medal, label: 'Top 3', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' },
    top10: { icon: Award, label: 'Top 10', className: 'bg-amber-700/20 text-amber-600 border-amber-700/50' },
    top15: { icon: Award, label: 'Top 15', className: 'bg-slate-700/20 text-slate-500 border-slate-700/50' },
    upvoted: { icon: Star, label: 'Upvoted', className: 'bg-purple-500/20 text-purple-400 border-purple-500/50' },
  };
  
  const badge = badges[achievement];
  if (!badge) return null;
  
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge.className}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
};

// Judge Card Component - compact 3-line design matching reference screenshot
const JudgeCard = ({ judge }) => {
  const [imgError, setImgError] = useState(false);
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(judge.name)}&background=4F46E5&color=fff&size=64`;
  
  // Check if avatar URL looks valid (not pointing to dead servers)
  const isValidAvatarUrl = judge.avatar_url && 
    !judge.avatar_url.includes('pitch-buyers.emergent.host') &&
    !judge.avatar_url.includes('venturestage-1') &&
    !judge.avatar_url.includes('pitchmates.preview');
  
  const imgSrc = (!imgError && isValidAvatarUrl) ? judge.avatar_url : fallbackUrl;
  
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700/50 w-[200px] flex-shrink-0">
      <img 
        src={imgSrc} 
        alt={judge.name} 
        className="w-10 h-10 rounded-full object-cover border border-slate-600 flex-shrink-0"
        onError={() => setImgError(true)}
      />
      <div className="flex-1 min-w-0 overflow-hidden">
        <p className="font-medium text-white text-sm leading-tight truncate">{judge.name}</p>
        <p className="text-xs text-slate-400 leading-tight truncate">{judge.title || ''}</p>
        <p className="text-xs text-slate-500 leading-tight truncate">{judge.company || ''}</p>
      </div>
    </div>
  );
};

// Startup Row Component - links to individual product page
const StartupRow = ({ startup, rank, onUpvote, userUpvoted, activeCategory }) => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const [isUpvoting, setIsUpvoting] = useState(false);
  const navigate = useNavigate();

  const handleUpvote = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAuthenticated) {
      loginWithGoogle('/startups');
      return;
    }
    
    setIsUpvoting(true);
    try {
      await onUpvote(startup.company_id);
    } finally {
      setIsUpvoting(false);
    }
  };
  
  // Get slug for URL
  const slug = startup.slug || startup.company_id;
  
  // Truncate description
  const description = startup.tagline || startup.solution_statement || '';
  const shortDesc = description.length > 120 ? description.substring(0, 120) + '...' : description;
  
  // Save scroll position on mouse down (before click event triggers navigation)
  const handleMouseDown = (e) => {
    // Don't save scroll when clicking on CTA buttons
    if (e.target.closest('a[target="_blank"]') || e.target.closest('button')) return;
    
    // Use multiple methods to get scroll position reliably
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    sessionStorage.setItem('startupsScrollPosition', scrollY.toString());
    if (activeCategory) {
      sessionStorage.setItem('startupsActiveCategory', activeCategory);
    }
  };
  
  return (
    <Link 
      to={`/startups/${slug}${activeCategory ? `?from=${encodeURIComponent(activeCategory)}` : ''}`}
      onMouseDown={handleMouseDown}
      className="flex items-center gap-4 px-6 py-4 bg-slate-900/30 hover:bg-slate-800/50 border-b border-slate-800/50 transition-colors cursor-pointer group"
      data-testid={`startup-row-${startup.company_id}`}
    >
      {/* Rank Number */}
      {rank && (
        <div className="w-8 text-center flex-shrink-0">
          <span className="text-lg font-bold text-slate-500">{rank}</span>
        </div>
      )}
      
      {/* Logo - single blue/purple gradient for all */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {startup.logo_url ? (
          <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xl font-bold text-white">{startup.name?.[0] || '?'}</span>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h3 className="font-semibold text-white text-lg group-hover:text-blue-400 transition-colors">{startup.name}</h3>
          <AchievementBadge achievement={startup.selection_status} />
        </div>
        <p className="text-sm text-slate-400 line-clamp-2">{shortDesc}</p>
        {startup.category && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-xs">
            {startup.category}
          </span>
        )}
      </div>
      
      {/* CTA Button - Visit Website */}
      <a
        href={startup.website || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          if (!startup.website) e.preventDefault();
        }}
      >
        <Button 
          variant="outline"
          size="sm"
          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          disabled={!startup.website}
        >
          Visit Site
          <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      </a>
      
      {/* 
      === UPVOTE & COMMENT STATS (Hidden for now - re-enable when we have more engagement) ===
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-1 text-slate-500">
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm">{startup.comment_count || 0}</span>
        </div>
        <button
          className={`flex flex-col items-center px-3 py-2 rounded-lg border transition-all min-w-[60px] ${
            userUpvoted 
              ? 'bg-orange-500/20 border-orange-500 text-orange-400' 
              : 'border-slate-700 text-slate-400 hover:border-orange-500/50 hover:text-orange-400 hover:bg-slate-800'
          }`}
          onClick={handleUpvote}
          disabled={isUpvoting}
          data-testid={`upvote-btn-${startup.company_id}`}
        >
          {isUpvoting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
          <span className="font-semibold text-sm">{startup.upvote_count || 0}</span>
        </button>
      </div>
      */}
    </Link>
  );
};

// Startup Detail Modal
const StartupModal = ({ startup, onClose }) => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  
  useEffect(() => {
    if (startup) {
      loadComments();
    }
  }, [startup]);
  
  const loadComments = async () => {
    try {
      const data = await getComments(startup.company_id);
      setComments(data.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };
  
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    if (!isAuthenticated) {
      loginWithGoogle('/startups');
      return;
    }
    
    setSubmittingComment(true);
    try {
      const data = await addComment(startup.company_id, newComment);
      if (data.comment) {
        setComments([...comments, data.comment]);
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };
  
  // Generate a consistent color based on company name
  if (!startup) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
        data-testid="startup-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {startup.logo_url ? (
                <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-white">{startup.name?.[0]}</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="text-2xl font-bold text-white">{startup.name}</h2>
                <AchievementBadge achievement={startup.selection_status} />
              </div>
              <p className="text-slate-400">{startup.tagline}</p>
              <div className="flex items-center gap-2 mt-2">
                {startup.category && (
                  <span className="px-2 py-0.5 bg-slate-800 text-slate-300 rounded text-xs">{startup.category}</span>
                )}
                {startup.stage && (
                  <span className="px-2 py-0.5 bg-blue-900/50 text-blue-300 rounded text-xs">{startup.stage}</span>
                )}
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
              data-testid="close-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Solution - use tagline which comes from "100 Character Solutions (Cleaned)" */}
          {(startup.tagline || startup.solution_statement) && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-2">Solution</h3>
              <p className="text-slate-200 leading-relaxed">{startup.tagline || startup.solution_statement}</p>
            </div>
          )}
          
          {/* Founder - from Person Cleaned (Website) */}
          {startup.founder && startup.founder.name && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Founder</h3>
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(startup.founder.name)}&background=4F46E5&color=fff`} 
                  alt={startup.founder.name} 
                  className="w-12 h-12 rounded-full object-cover" 
                />
                <div className="flex-1">
                  <p className="font-medium text-white">{startup.founder.name}</p>
                  {startup.founder.title && (
                    <p className="text-sm text-slate-400">{startup.founder.title}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* CTAs */}
          <div className="flex gap-3">
            {startup.demo_url && (
              <a href={startup.demo_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Demo
                </Button>
              </a>
            )}
            {startup.website && (
              <a href={startup.website} target="_blank" rel="noopener noreferrer" className={startup.demo_url ? '' : 'flex-1'}>
                <Button variant="outline" className={`${startup.demo_url ? '' : 'w-full'} border-slate-600 text-slate-300 hover:bg-slate-700`}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Website
                </Button>
              </a>
            )}
            {startup.linkedin_url && (
              <a href={startup.linkedin_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white">
                  <Linkedin className="w-4 h-4" />
                </Button>
              </a>
            )}
          </div>
          
          {/* Comments Section */}
          <div className="border-t border-slate-700 pt-6">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">
              Discussion ({comments.length})
            </h3>
            
            {/* Comment Form */}
            <form onSubmit={handleSubmitComment} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={isAuthenticated ? "Share your thoughts..." : "Sign in to comment"}
                  className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  disabled={!isAuthenticated}
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  disabled={!newComment.trim() || submittingComment}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500"
                  data-testid="submit-comment"
                >
                  {submittingComment ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            </form>
            
            {/* Comments List */}
            <div className="space-y-3">
              {loadingComments ? (
                <div className="text-center py-4 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No comments yet. Start the conversation!
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.comment_id} className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <img 
                        src={comment.author?.avatar_url || `https://ui-avatars.com/api/?name=User&background=4F46E5&color=fff`}
                        alt={comment.author?.name || 'User'}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm font-medium text-white">{comment.author?.name || 'Anonymous'}</span>
                      <span className="text-xs text-slate-500">
                        {comment.created_at && new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300">{comment.body}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Event Section Component - matches the original beautiful UI
const EventSection = ({ event, isFirst, companies, upvotedMap, onUpvote, activeCategory }) => {
  const [showAll, setShowAll] = useState(false);
  
  // Limit to 10 visible records initially, show all when expanded
  const visibleLimit = 10;
  const displayed = showAll ? companies : companies.slice(0, visibleLimit);
  const hiddenCount = companies.length - visibleLimit;
  
  // Use judges from event data (populated from Panel columns)
  const judges = event.judges || [];
  
  return (
    <section id={event.slug} className="mb-4" data-testid={`event-section-${event.event_id}`}>
      {/* Sticky Event Header */}
      <div className={`sticky ${isFirst ? 'top-[65px]' : 'top-[65px]'} z-40 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800`}>
        <div className="max-w-5xl mx-auto px-6 py-2">
          {/* Event Title Row with Sponsors on right */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-white">{event.name}</h2>
                {event.date && (
                  <span className="text-slate-400 text-sm flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              {event.panel_title && (
                <p className="text-blue-400 text-sm mt-0.5">{event.panel_title}</p>
              )}
            </div>
            
            {/* Sponsors - top right */}
            {event.sponsors && event.sponsors.length > 0 && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium hidden sm:inline">Sponsored by</span>
                {event.sponsors.map((sponsor, i) => (
                  <a 
                    key={i}
                    href={sponsor.website_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium transition-colors bg-slate-800/50 text-slate-400 hover:bg-slate-700 hover:text-white"
                    title={sponsor.name}
                  >
                    {sponsor.name}
                  </a>
                ))}
              </div>
            )}
          </div>
          
          {/* Judges Row */}
          {judges.length > 0 && (
            <div className="mt-2.5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 font-medium">Event Judges</p>
              <div className="flex gap-2 overflow-x-auto pb-0.5">
                {judges.map((judge, i) => (
                  <JudgeCard key={i} judge={judge} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Startup List */}
      <div className="max-w-5xl mx-auto">
        {displayed.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Rocket className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No ranked startups for this event yet</p>
          </div>
        ) : (
          displayed.map((startup, idx) => (
            <StartupRow 
              key={startup.company_id} 
              startup={startup}
              rank={idx + 1}
              onUpvote={onUpvote}
              userUpvoted={upvotedMap[startup.company_id]}
              activeCategory={activeCategory}
            />
          ))
        )}
        
        {/* Show More Button - only show if there are more than 10 records */}
        {hiddenCount > 0 && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full py-4 text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2 hover:bg-slate-800/30 transition-colors border-b border-slate-800/50"
            data-testid="show-more-btn"
          >
            <ChevronDown className="w-4 h-4" />
            Show {hiddenCount} More Applicants
          </button>
        )}
        
        {showAll && hiddenCount > 0 && (
          <button
            onClick={() => setShowAll(false)}
            className="w-full py-4 text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2 hover:bg-slate-800/30 transition-colors border-b border-slate-800/50"
          >
            <ChevronUp className="w-4 h-4" />
            Show Top 10 Only
          </button>
        )}
      </div>
    </section>
  );
};

// Main Page Component
const StartupsPage = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [eventCompanies, setEventCompanies] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || null);
  const [loading, setLoading] = useState(true);
  const [upvotedMap, setUpvotedMap] = useState({});
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);

  useEffect(() => {
    loadEvents();
    
    // Check if we should restore scroll position
    const savedPosition = sessionStorage.getItem('startupsScrollPosition');
    const savedCategory = sessionStorage.getItem('startupsActiveCategory');
    
    if (savedPosition) {
      setShouldRestoreScroll(true);
      // Restore category if available
      if (savedCategory) {
        setActiveCategory(savedCategory);
      }
    }
  }, []);
  
  // Restore scroll position after content is loaded
  useEffect(() => {
    if (!loading && shouldRestoreScroll) {
      const savedPosition = sessionStorage.getItem('startupsScrollPosition');
      if (savedPosition) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
          // Clear saved position after restoring
          sessionStorage.removeItem('startupsScrollPosition');
          sessionStorage.removeItem('startupsActiveCategory');
          setShouldRestoreScroll(false);
        }, 100);
      }
    }
  }, [loading, shouldRestoreScroll]);

  // Sync URL with active category
  useEffect(() => {
    if (activeCategory) {
      setSearchParams({ category: activeCategory });
    }
  }, [activeCategory, setSearchParams]);

  // Load upvote status when user is authenticated
  useEffect(() => {
    if (isAuthenticated && Object.keys(eventCompanies).length > 0) {
      loadUpvoteStatuses();
    }
  }, [isAuthenticated, eventCompanies]);

  const loadEvents = async () => {
    try {
      // Use fast endpoint that returns all data in ONE query
      const data = await getEventsFast();
      const eventsData = data.events || [];
      
      // Extract unique categories
      const cats = [...new Set(eventsData.map(e => e.category).filter(Boolean))];
      setCategories(cats);
      
      // Use category from URL or default to first
      const urlCategory = searchParams.get('category');
      if (urlCategory && cats.includes(urlCategory)) {
        setActiveCategory(urlCategory);
      } else if (cats.length > 0 && !activeCategory) {
        setActiveCategory(cats[0]);
      }
      
      setEvents(eventsData);
      
      // Companies are already embedded in the events from the fast endpoint
      const companiesMap = {};
      for (const event of eventsData) {
        companiesMap[event.event_id] = event.startups || [];
      }
      setEventCompanies(companiesMap);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUpvoteStatuses = async () => {
    // Get all company IDs at once
    const allCompanyIds = [];
    for (const eventId of Object.keys(eventCompanies)) {
      for (const company of eventCompanies[eventId]) {
        allCompanyIds.push(company.company_id);
      }
    }
    
    // Skip if no companies
    if (allCompanyIds.length === 0) return;
    
    try {
      // Single batch API call instead of 50+ individual calls
      const result = await getBatchUpvoteStatus(allCompanyIds);
      setUpvotedMap(result.upvotes || {});
    } catch (err) {
      console.error('Error loading upvote statuses:', err);
    }
  };

  const handleUpvote = async (companyId) => {
    try {
      const result = await toggleUpvote(companyId);
      
      setUpvotedMap(prev => ({
        ...prev,
        [companyId]: result.upvoted
      }));
      
      setEventCompanies(prev => {
        const newMap = { ...prev };
        for (const eventId of Object.keys(newMap)) {
          newMap[eventId] = newMap[eventId].map(c => {
            if (c.company_id === companyId) {
              return {
                ...c,
                upvote_count: (c.upvote_count || 0) + (result.upvoted ? 1 : -1)
              };
            }
            return c;
          });
        }
        return newMap;
      });
    } catch (error) {
      console.error('Error toggling upvote:', error);
    }
  };

  const filteredEvents = events.filter(e => !activeCategory || e.category === activeCategory);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Shared Navigation Header with My Profile */}
      <NavHeader />
      
      {/* Category Tabs */}
      <div className="border-b border-slate-800 bg-slate-900/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1 text-sm font-medium rounded-md whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`category-tab-${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
          <p className="text-slate-400">Loading startups...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-20">
          <Rocket className="w-16 h-16 mx-auto mb-4 text-slate-700" />
          <h2 className="text-2xl font-bold text-white mb-4">No Events Yet</h2>
          <p className="text-slate-400 mb-8">Check back soon for upcoming pitch events!</p>
          <Link to="/#application">
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0">
              Apply to Pitch
            </Button>
          </Link>
        </div>
      ) : (
        /* Events for Active Category */
        <div className="py-6">
          {filteredEvents.map((event, idx) => (
            <EventSection 
              key={event.event_id} 
              event={event} 
              isFirst={idx === 0}
              companies={eventCompanies[event.event_id] || []}
              upvotedMap={upvotedMap}
              onUpvote={handleUpvote}
              activeCategory={activeCategory}
            />
          ))}
        </div>
      )}
      
      {/* Footer CTA */}
      <section className="py-16 text-center border-t border-slate-800 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white mb-3">Want to Pitch at Our Next Event?</h2>
          <p className="text-slate-400 mb-6">Get in front of enterprise buyers and decision makers.</p>
          <Link to="/#application">
            <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0">
              Apply to Pitch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StartupsPage;
